import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SearchFilter from './SearchFilter';

const HierarchicalTreeSelector = ({ categories, selectedItem: externalSelectedItem, onSelectionChange, products = [], onFilterChange = null }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [selectedItem, setSelectedItem] = useState(null);

  // Sync internal state with external selection
  useEffect(() => {
    setSelectedItem(externalSelectedItem);
  }, [externalSelectedItem]);

  // Helper function to find a category by ID
  const findCategoryById = (categoryId) => {
    return categories.find(cat => cat.id === categoryId);
  };


  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const toggleExpanded = (id, type) => {
    const key = `${type}-${id}`;
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedItems(newExpanded);
  };

  const handleSelection = (id, type, item, parentCategoryId = null) => {
    // If clicking the same item, unselect it
    if (selectedItem?.id === id && selectedItem?.type === type) {
      // When unselecting a subcategory, navigate to its parent category
      if (type === 'subcategory' && selectedItem.parentCategoryId) {
        const parentCategory = findCategoryById(selectedItem.parentCategoryId);
        if (parentCategory) {
          const parentSelection = { 
            id: parentCategory.id, 
            type: 'category', 
            item: parentCategory 
          };
          setSelectedItem(parentSelection);
          onSelectionChange(parentSelection);
          return;
        }
      }
      
      // For categories or if parent not found, clear selection
      setSelectedItem(null);
      onSelectionChange(null);
      return;
    }
    
    // Otherwise, select the new item (radio behavior - only one selected)
    const selection = { id, type, item, parentCategoryId };
    setSelectedItem(selection);
    onSelectionChange(selection);
  };

  const isSelected = (id, type) => {
    return selectedItem?.id === id && selectedItem?.type === type;
  };

  const isInSelectionPath = (id, type) => {
    // Check if this item is in the path to the selected item
    if (isSelected(id, type)) {
      return true;
    }
    
    if (!selectedItem || selectedItem.type !== 'subcategory') {
      return false;
    }
    
    // If a subcategory is selected, check if this category is its parent
    if (type === 'category' && selectedItem.parentCategoryId === id) {
      return true;
    }
    
    // If a subcategory is selected, check if this subcategory is in its parent hierarchy
    if (type === 'subcategory') {
      return isSubcategoryParentOf(id, selectedItem.id);
    }
    
    return false;
  };

  const isSubcategoryParentOf = (parentId, childId) => {
    // Find the child subcategory and check if parentId is in its hierarchy
    const findSubcategoryPath = (subs, targetId, currentPath = []) => {
      if (!subs) return null;
      
      for (const sub of subs) {
        const newPath = [...currentPath, sub.id];
        
        if (sub.id === targetId) {
          return newPath;
        }
        
        if (sub.children) {
          const foundPath = findSubcategoryPath(sub.children, targetId, newPath);
          if (foundPath) return foundPath;
        }
      }
      return null;
    };
    
    // Search through all categories
    for (const category of categories) {
      const path = findSubcategoryPath(category.subcategories, childId);
      if (path && path.includes(parentId)) {
        return true;
      }
    }
    
    return false;
  };

  const shouldShowSubcategories = (categoryId) => {
    // Show subcategories if category is selected OR if a subcategory within this category is selected
    return isSelected(categoryId, 'category') || 
           (selectedItem?.type === 'subcategory' && selectedItem?.parentCategoryId === categoryId);
  };

  const shouldShowChildSubcategories = (subcategoryId) => {
    // Show child subcategories if this subcategory is selected OR if one of its children is selected
    if (isSelected(subcategoryId, 'subcategory')) {
      return true;
    }
    
    // Check if any child of this subcategory is selected
    if (selectedItem?.type === 'subcategory') {
      // We need to check if the selected item is a child of this subcategory
      // This is a bit tricky since we need to traverse the hierarchy
      const checkIfChildSelected = (subs, targetId) => {
        if (!subs) return false;
        for (const sub of subs) {
          if (sub.id === targetId) return true;
          if (sub.children && checkIfChildSelected(sub.children, targetId)) return true;
        }
        return false;
      };
      
      // Find this subcategory in the categories to get its children
      for (const category of categories) {
        const findSubcategory = (subs) => {
          if (!subs) return null;
          for (const sub of subs) {
            if (sub.id === subcategoryId) return sub;
            const found = findSubcategory(sub.children);
            if (found) return found;
          }
          return null;
        };
        
        const subcategory = findSubcategory(category.subcategories);
        if (subcategory && checkIfChildSelected(subcategory.children, selectedItem.id)) {
          return true;
        }
      }
    }
    
    return false;
  };

  const isExpanded = (id, type) => {
    return expandedItems.has(`${type}-${id}`);
  };

  const filterItems = (items, searchTerm) => {
    if (!searchTerm) return items;
    return items.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderSubcategories = (subcategories, level = 0, parentCategoryId = null) => {
    const filteredSubs = filterItems(subcategories, searchTerm);
    
    return filteredSubs.map(subcategory => (
      <div key={subcategory.id} className={`mb-2 ${level > 0 ? 'ml-6' : ''}`}>
        <label className="flex items-center space-x-2 font-medium cursor-pointer">
          <input
            type="checkbox"
            checked={isInSelectionPath(subcategory.id, 'subcategory')}
            onChange={() => handleSelection(subcategory.id, 'subcategory', subcategory, parentCategoryId)}
            className="text-orange-500"
          />
          <span>{subcategory.name}</span>
        </label>
        
        {shouldShowChildSubcategories(subcategory.id) && subcategory.children && subcategory.children.length > 0 && (
          <div className="ml-6 mt-1">
            {renderSubcategories(subcategory.children, level + 1, parentCategoryId)}
          </div>
        )}
      </div>
    ));
  };


  const filteredCategories = filterItems(categories, searchTerm);

  return (
    <div className="p-4 border rounded">
      {filteredCategories.map(category => (
        <div key={category.id} className="mb-4">
          <label className="flex items-center space-x-2 font-medium cursor-pointer">
            <input
              type="checkbox"
              checked={isInSelectionPath(category.id, 'category')}
              onChange={() => handleSelection(category.id, 'category', category)}
              className="text-orange-500"
            />
            <span>{category.name}</span>
          </label>
          
          {shouldShowSubcategories(category.id) && category.subcategories && (
            <div className="ml-6 mt-2">
              {renderSubcategories(category.subcategories, 0, category.id)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

HierarchicalTreeSelector.propTypes = {
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      subcategories: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.number.isRequired,
          name: PropTypes.string.isRequired,
          children: PropTypes.array,
          is_leaf: PropTypes.bool
        })
      )
    })
  ).isRequired,
  selectedItem: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.string,
    item: PropTypes.object,
    parentCategoryId: PropTypes.number
  }),
  onSelectionChange: PropTypes.func.isRequired,
  products: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
      filter_values: PropTypes.arrayOf(PropTypes.string)
    })
  ),
  onFilterChange: PropTypes.func
};

export default HierarchicalTreeSelector;